// Draws a Mermaid diagram in the browser. Mermaid is ~1MB, so it is fetched
// from the CDN on the first diagram a page shows and never bundled.

export const MERMAID_CDN_URL =
	"https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.esm.min.mjs"

export type MermaidTheme = "light" | "dark"

interface MermaidApi {
	initialize: (config: Record<string, unknown>) => void
	render: (id: string, source: string) => Promise<{ svg: string }>
}

let loading: Promise<MermaidApi> | null = null
let renderCount = 0

export function loadMermaid(
	importer: (url: string) => Promise<unknown> = (url) =>
		import(/* webpackIgnore: true */ /* @vite-ignore */ url),
): Promise<MermaidApi> {
	if (!loading) {
		loading = importer(MERMAID_CDN_URL)
			.then((module) => {
				const api = ((module as { default?: MermaidApi }).default ??
					module) as MermaidApi
				if (typeof api?.render !== "function") {
					throw new Error("Mermaid did not load")
				}
				return api
			})
			.catch((error: unknown) => {
				// A failed fetch (offline, blocked CDN) can be retried next time.
				loading = null
				throw error
			})
	}
	return loading
}

export function currentMermaidTheme(doc: Document = document): MermaidTheme {
	return doc.documentElement.classList.contains("dark") ? "dark" : "light"
}

// Renders run one at a time: Mermaid keeps one global config, and two diagrams
// drawn with different themes at once would mix them.
let queue: Promise<unknown> = Promise.resolve()

export function renderMermaidSvg(
	source: string,
	theme: MermaidTheme,
	importer?: (url: string) => Promise<unknown>,
): Promise<string> {
	const run = async () => {
		const mermaid = await loadMermaid(importer)
		mermaid.initialize({
			startOnLoad: false,
			securityLevel: "strict",
			theme: theme === "dark" ? "dark" : "default",
			fontFamily: "inherit",
		})
		renderCount += 1
		const { svg } = await mermaid.render(`pl-mermaid-${renderCount}`, source)
		return svg
	}
	const next = queue.then(run, run)
	queue = next.catch(() => undefined)
	return next
}

export function resetMermaidLoaderForTests(): void {
	loading = null
	queue = Promise.resolve()
}
